import React, { useState, useEffect, useCallback } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Wallet, TrendingUp, ShoppingBag, RefreshCw, Loader2,
  Banknote, Landmark, CreditCard, Armchair, Truck, Trophy,
  FileSpreadsheet, FileText, MapPin, UserRound, ArrowLeft,
} from "lucide-react";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PAYMENT_META = {
  efectivo: { label: "Efectivo", icon: Banknote },
  transferencia: { label: "Transferencia", icon: Landmark },
  tarjeta: { label: "Tarjeta", icon: CreditCard },
  sin_especificar: { label: "Sin especificar", icon: Wallet },
};

function fmt(n) {
  return Number(n ?? 0).toLocaleString("es-CO");
}
function fmtCOP(n) {
  return `$${fmt(n)}`;
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
}

function KpiCard({ icon: Icon, label, value, sub, color = "supernova" }) {
  const colorMap = {
    supernova: "bg-supernova-50 text-supernova-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
  };
  return (
    <div className="bg-white rounded-2xl shadow p-5 flex items-start gap-4">
      <div className={`p-3 rounded-xl flex-shrink-0 ${colorMap[color]}`}>
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-xl sm:text-2xl font-bold text-gray-800 leading-tight mt-0.5 break-words">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function getCompanyIdFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.companyId ?? u?.company?.id ?? null;
  } catch { return null; }
}

export default function CompanyDailyCash() {
  const companyId = getCompanyIdFromStorage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState("");
  const [staff, setStaff] = useState([]);
  const [meseroId, setMeseroId] = useState("");
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'pdf' | null

  useEffect(() => {
    if (!companyId) return;
    API.get(`/sedes/company/${companyId}`)
      .then((res) => setSedes(res.data?.data?.sedes ?? res.data?.data ?? []))
      .catch(() => {});
    API.get(`/companies/${companyId}/users`)
      .then((res) => setStaff(res.data?.data?.users ?? []))
      .catch(() => {});
  }, [companyId]);

  const buildParams = useCallback(() => {
    const params = {};
    if (sedeId) params.sedeId = sedeId;
    if (meseroId) params.userId = meseroId;
    if (fromDate) params.from = fromDate;
    if (toDate) {
      const d = new Date(toDate);
      d.setDate(d.getDate() + 1);
      params.to = d.toISOString().slice(0, 10);
    }
    return params;
  }, [sedeId, meseroId, fromDate, toDate]);

  const load = useCallback(async () => {
    if (!companyId || !UUID_RE.test(companyId)) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await API.get(`/metrics/company/${companyId}/daily-cash`, { params: buildParams() });
      setReport(res.data?.data ?? null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cargando el reporte");
    } finally {
      setLoading(false);
    }
  }, [companyId, buildParams]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await API.get(`/metrics/company/${companyId}/daily-cash/export`, {
        params: { ...buildParams(), format },
        responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte-ventas.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error generando el archivo");
    } finally {
      setExporting(null);
    }
  };

  const selectedMesero = staff.find((u) => u.id === meseroId);
  const isToday = fromDate === todayStr() && toDate === todayStr();

  if (loading && !report) {
    return (
      <div className="flex justify-center mt-20">
        <Loader2 size={32} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-xl mx-auto mt-20 text-center text-gray-400">
        <p>No se pudo cargar el reporte.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wallet className="text-supernova-600" size={26} /> Caja y reportes
          </h2>
          <p className="text-sm text-gray-500 mt-1">{isToday ? "Hoy" : `${fromDate} al ${toDate}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport("xlsx")}
            disabled={exporting !== null}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition disabled:opacity-50"
          >
            {exporting === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
          </button>
          <button
            onClick={() => handleExport("pdf")}
            disabled={exporting !== null}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition disabled:opacity-50"
          >
            {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Desde</label>
          <input type="date" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Hasta</label>
          <input type="date" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        {sedes.length > 1 && (
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Sede</label>
            <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white" value={sedeId} onChange={(e) => setSedeId(e.target.value)}>
              <option value="">Todas las sedes</option>
              {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Mesero</label>
          <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white" value={meseroId} onChange={(e) => setMeseroId(e.target.value)}>
            <option value="">Todos</option>
            {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition">
          <RefreshCw size={14} /> Aplicar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={TrendingUp} color="green" label="Total vendido" value={fmtCOP(report.totalRevenue)} />
        <KpiCard icon={ShoppingBag} color="blue" label="Pedidos cobrados" value={fmt(report.orderCount)} />
        <KpiCard icon={Armchair} color="purple" label="Mesas" value={fmtCOP(report.byType?.mesa?.revenue)} sub={`${fmt(report.byType?.mesa?.count)} cuentas`} />
        <KpiCard icon={Truck} color="supernova" label="Domicilio / recogida" value={fmtCOP(report.byType?.otros?.revenue)} sub={`${fmt(report.byType?.otros?.count)} pedidos`} />
      </div>

      {/* Payment breakdown */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h3 className="text-base font-bold text-gray-700 mb-3">Por método de pago</h3>
        {(report.byPaymentMethod ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Sin ventas cobradas en este rango</p>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3">
            {report.byPaymentMethod.map((p) => {
              const meta = PAYMENT_META[p.method] ?? { label: p.method, icon: Wallet };
              return (
                <div key={p.method} className="border border-gray-100 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center shrink-0">
                    <meta.icon size={17} className="text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">{meta.label}</p>
                    <p className="font-bold text-gray-800">{fmtCOP(p.revenue)}</p>
                    <p className="text-[11px] text-gray-400">{p.count} pedido{p.count !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Por sede */}
      {sedes.length > 1 && !sedeId && (
        <div className="bg-white rounded-2xl shadow p-5">
          <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
            <MapPin size={16} className="text-supernova-500" /> Por sede
          </h3>
          {(report.bySede ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin ventas en este rango</p>
          ) : (
            <div className="space-y-2">
              {report.bySede.map((s) => (
                <div key={s.sedeId} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0">
                  <span className="text-gray-700">{s.name}</span>
                  <span className="text-gray-400">{s.count} pedido{s.count !== 1 ? "s" : ""}</span>
                  <span className="font-bold text-gray-800">{fmtCOP(s.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Por mesero / historial */}
      <div className="bg-white rounded-2xl shadow p-5">
        {meseroId ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-700 flex items-center gap-2">
                <UserRound size={16} className="text-supernova-500" /> Historial de {selectedMesero?.name ?? "mesero"}
              </h3>
              <button onClick={() => setMeseroId("")} className="text-xs text-supernova-600 hover:text-supernova-700 flex items-center gap-1">
                <ArrowLeft size={12} /> Ver todos los meseros
              </button>
            </div>
            {(report.mesaHistory ?? []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Sin cuentas de mesa atendidas en este rango</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {report.mesaHistory.map((h) => (
                  <div key={h.orderNumber} className="flex items-center justify-between gap-3 text-sm border-b border-gray-50 pb-2 last:border-0">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800">Mesa {h.tableLabel} · {h.cliente}</p>
                      <p className="text-xs text-gray-400">{fmtDateTime(h.deliveredAt)} · {PAYMENT_META[h.paymentMethod]?.label ?? h.paymentMethod}</p>
                    </div>
                    <span className="font-bold text-gray-800 shrink-0">{fmtCOP(h.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
              <UserRound size={16} className="text-supernova-500" /> Por mesero
            </h3>
            {(report.byMesero ?? []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Sin cuentas de mesa en este rango</p>
            ) : (
              <div className="space-y-2">
                {report.byMesero.map((m) => (
                  <button
                    key={m.userId}
                    onClick={() => setMeseroId(m.userId)}
                    className="w-full flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0 hover:bg-gray-50 rounded-lg px-1 -mx-1"
                  >
                    <span className="text-gray-700">{m.name}</span>
                    <span className="text-gray-400">{m.count} cuenta{m.count !== 1 ? "s" : ""}</span>
                    <span className="font-bold text-gray-800">{fmtCOP(m.revenue)}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Top products */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
          <Trophy size={16} className="text-supernova-500" /> Más vendidos
        </h3>
        {(report.topProducts ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Sin productos vendidos en este rango</p>
        ) : (
          <div className="space-y-2">
            {report.topProducts.map((p, i) => (
              <div key={p.id || p.title} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="flex-1 text-sm text-gray-700 truncate">{p.title}</span>
                <span className="text-xs text-gray-400">{p.qty} uds</span>
                <span className="text-sm font-bold text-gray-800 w-24 text-right">{fmtCOP(p.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
